import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BookingV2ContractDialogComponent } from '../../../contract/booking-v2-contract-dialog.component';
import { FormsModule } from '@angular/forms';
import { catchError, filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Select } from 'primeng/select';

import { FilesApiService } from '@bcmApiServices/files.api-service';
import { BcmDocumentCategoriesFacade } from '@bcmServices/documents/bcm-document-categories-facade';
import { AppNotificationService } from '@core/services/app-notification.service';
import { UserService } from '@core/services/user.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { TranslationModule } from '@core/translation/translation.module';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import { naturalCompare } from '@shared/functions/natural-sort';
import { IFile } from '@shared/interfaces/file';
import { BcmDocumentCategory } from '@shared/models/bcm-document-category';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';

import { BookingV2BookingDocument } from '../../../dtos/booking-v2.dtos';
import { BookingV2Facade } from '../../../services/booking-v2.facade';

@Component({
    selector: 'booking-v2-documents-tab',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DatePipe, FormsModule, Button, ButtonIcon, ButtonLabel, InputText, ProgressSpinner, Select, TranslationModule, U2bIconButtonComponent, BookingV2ContractDialogComponent],
    templateUrl: './documents-tab.component.html',
    host: { class: 'block' },
})
export class DocumentsTabComponent {

    private readonly facade = inject(BookingV2Facade);
    private readonly filesApi = inject(FilesApiService);
    private readonly notifications = inject(AppNotificationService);
    private readonly categoriesFacade = inject(BcmDocumentCategoriesFacade);
    private readonly userService = inject(UserService);

    readonly readOnly = input<boolean>(false);

    // Same gate as the backend route, so users without it don't get a 403 toast.
    private readonly canReadCategories = this.userService.hasOnePermissionOf(
        BcmUserPermission.FULL_GRANT,
        BcmUserPermission.DOCUMENTS_READ,
    );

    // Saved rows: the backend only removes them with the delete right. Unsaved uploads stay removable.
    readonly canDeleteDocuments = this.userService.hasOnePermissionOf(
        BcmUserPermission.FULL_GRANT,
        BcmUserPermission.DOCUMENTS_DELETE,
    );

    readonly documents = this.facade.documents;

    // Contract generator (edit mode only — needs a saved booking + a booking person).
    readonly contractDialogOpen = signal<boolean>(false);
    readonly contractBookingId = this.facade.bookingId;
    readonly contractPerson = this.facade.selectedPerson;
    readonly contractBoat = this.facade.selectedBoat;
    readonly contractBerths = this.facade.selectedBerths;
    readonly contractFrom = computed(() => this.facade.dateRange().from);
    readonly contractTo = computed(() => this.facade.dateRange().to);
    readonly canCreateContract = computed(() =>
        !this.readOnly() && this.facade.isEditMode() && this.facade.selectedPerson() != null,
    );

    readonly showForm = signal<boolean>(false);

    // Loaded once the add form is first opened. undefined = not loaded yet.
    readonly categoryOptions = toSignal(
        toObservable(this.showForm).pipe(
            filter(Boolean),
            take(1),
            switchMap(() => this.canReadCategories
                ? this.categoriesFacade.getAllUnpaged().pipe(
                    map(categories => [...categories].sort((a, b) => naturalCompare(a.name, b.name))),
                    catchError(() => of<BcmDocumentCategory[]>([])),
                )
                : of<BcmDocumentCategory[]>([])),
        ),
        { initialValue: undefined },
    );

    // Hidden when no categories can be loaded; the backend then files the document under "Sonstiges".
    readonly showCategory = computed(() => {
        const options = this.categoryOptions();
        return options === undefined || options.length > 0;
    });

    private readonly draftSync = effect(() =>
        // Mark the advanced step dirty while the add form is open & unsaved, so
        // navigating away (tab/step/dialog) prompts to discard.
        this.facade.advancedDraftDirty.set(!this.readOnly() && this.showForm()),
    );
    readonly name = signal<string>('');
    readonly category = signal<BcmDocumentCategory | null>(null);
    readonly selectedFile = signal<File | null>(null);
    readonly uploading = signal<boolean>(false);

    private readonly categoryValid = computed(() => !this.showCategory() || this.category() != null);

    readonly canAdd = computed(() =>
        !!this.selectedFile() && this.name().trim().length > 0 && this.categoryValid() && !this.uploading(),
    );

    toggleForm(): void {
        this.showForm.update(v => !v);
    }

    openContractDialog(): void {
        this.contractDialogOpen.set(true);
    }

    onContractVisibleChange(open: boolean): void {
        this.contractDialogOpen.set(open);
    }

    // The backend files the generated contract PDF as a booking document. Refreshing
    // the (facade-held) document list would need a booking re-fetch — deferred; the
    // PDF is downloaded/mailed regardless.
    onContractCreated(): void {
        this.contractDialogOpen.set(false);
    }

    closeForm(): void {
        this.showForm.set(false);
        this.name.set('');
        this.category.set(null);
        this.selectedFile.set(null);
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;
        this.selectedFile.set(file);
        // Prefill the title from the file name (without extension) when empty.
        if (file && !this.name().trim()) {
            this.name.set(file.name.replace(/\.[^.]+$/, ''));
        }
        // Reset so picking the same file again still fires (change) the next time.
        input.value = '';
    }

    addDocument(): void {
        const file = this.selectedFile();
        if (!file || !this.canAdd()) {
            return;
        }
        const name = this.name().trim();
        const category = this.showCategory() ? this.category() : null;
        this.uploading.set(true);

        // Upload the raw file to bcm_files now; the commit references it by id.
        // FilesApiService only reads `.file` off the IFile, so a thin wrapper is
        // enough.
        this.filesApi
            .addAndFetch({ file } as unknown as IFile)
            .pipe(finalize(() => this.uploading.set(false)))
            .subscribe({
                next: uploaded => {
                    const doc: BookingV2BookingDocument = {
                        id: `DOC${Date.now()}`,
                        name,
                        categoryId: category?.id ?? null,
                        categoryName: category?.name ?? null,
                        bcmFilesId: uploaded.id,
                        fileName: file.name,
                        mimeType: file.type || undefined,
                        size: file.size,
                        uploadDate: new Date(),
                    };
                    this.facade.documents.update(list => [...list, doc]);
                    this.closeForm();
                },
                error: () => this.notifications.showError(
                    TolgeeService.translate('bookingV2.advanced.documents.uploadError'),
                ),
            });
    }

    removeDocument(id: string): void {
        this.facade.documents.update(list => list.filter(d => d.id !== id));
    }

    formatBytes(bytes: number | undefined): string {
        if (!bytes) {
            return '';
        }
        return bytes > 1_000_000
            ? `${(bytes / 1_000_000).toFixed(1)} MB`
            : `${Math.round(bytes / 1000)} KB`;
    }
}
